import './index.css';
import { jsx } from 'react/jsx-runtime';
import { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { useDomStoredUniqueId } from '@hh.ru/magritte-common-dom-storage';

const FormHelperStateContext = createContext({
    wantToShowDescription: false,
    wantToShowErrorMessage: false,
});
const FormHelperStateProvider = ({ wantToShowDescription, wantToShowErrorMessage, setChildrenHelperState, children, }) => {
    const { wantToShowDescription: parentWantToShowDescription, wantToShowErrorMessage: parentWantToShowErrorMwssage } = useContext(FormHelperStateContext);
    return (jsx(FormHelperStateContext.Provider, { value: {
            wantToShowDescription: wantToShowDescription || parentWantToShowDescription,
            wantToShowErrorMessage: wantToShowErrorMessage || parentWantToShowErrorMwssage,
            setChildrenHelperState,
        }, children: children }));
};
const doesWantToShowErrorMessage = (errorMessage, invalid, disabled) => !!errorMessage && invalid && !disabled;
const isErrorVisible = (wantToShowErrorMessage, childrenErrorVisible) => wantToShowErrorMessage && !childrenErrorVisible;
const doesWantToShowDescription = (description, invalid, errorMessage, disabled) => !!description && (!(invalid && errorMessage) || disabled);
const isDescriptionVisible = (isWantToShowDescription, childrenErrorVisible, childrenDescriptionVisible, parentWantToShowErrorMessage) => isWantToShowDescription && !childrenDescriptionVisible && !childrenErrorVisible && !parentWantToShowErrorMessage;
/**
 * Хук для управления состоянием подсказок формы
 * Управляет состоянием отображения подсказок в зависимости от переданных пропсов и состояния в дочерних компонентах
 * отвечающих за отображение подсказок. Кроме того передает родительским компонентам подсказок информацию о своем
 * состоянии и состоянии дочерних компонентов подсказок.
 *
 * Реализует следующую логику:
 * 1. Если у компонента есть описание и нет ошибки, то отображается описание.
 * 2. Если у компонента есть ошибка и он невалиден, то отображается ошибка.
 * 3. Если несколько компонентов-подсказок вложенны один в другой, то отображать подсказку или ошибку одновременно
 *      может только один из них.
 * 5. Если несколько компонентов-подсказок вложенны один в другой, то приоритет для отображения имеет компонент
 *      с наибольшей глубиной вложенности.
 * 6. Отображение ошибок имеет более высокий приоритет, чем отображение описаний полей
 *
 * Иными словами, если есть несколько компонентов вложенных один в другой, и хотя бы один из них имеет ошибку,
 * то отображается только ошибка у компонента с наибольшей глубиной вложенности (или у нескольких если
 * они находятся на одной глубине). Если нет ошибок, то отображается только описание у компонента с наибольшей
 * глубиной вложенности (или у нескольких если они находятся на одной глубине).
 */
const useFormHelperState = ({ description, errorMessage, invalid = false, disabled = false, }) => {
    const id = useDomStoredUniqueId();
    const childrensWithVisibleErrors = useRef(new Set());
    const childrensWithVisibleDescriptions = useRef(new Set());
    const { setChildrenHelperState: stateChangeCallback, wantToShowErrorMessage: parentErrorVisible } = useContext(FormHelperStateContext);
    const wantToShowDescription = doesWantToShowDescription(description, invalid, errorMessage, disabled);
    const wantToShowErrorMessage = doesWantToShowErrorMessage(errorMessage, invalid, disabled);
    const [descriptionVisible, setDescriptionVisible] = useState(isDescriptionVisible(wantToShowDescription, childrensWithVisibleErrors.current.size > 0, childrensWithVisibleDescriptions.current.size > 0, parentErrorVisible));
    const [errorMessageVisible, setErrorMessageVisible] = useState(isErrorVisible(wantToShowErrorMessage, childrensWithVisibleErrors.current.size > 0));
    const updateState = useCallback(() => {
        const selfDescriptionVisible = isDescriptionVisible(wantToShowDescription, childrensWithVisibleErrors.current.size > 0, childrensWithVisibleDescriptions.current.size > 0, parentErrorVisible);
        const selfErrorVisible = isErrorVisible(wantToShowErrorMessage, childrensWithVisibleErrors.current.size > 0);
        setDescriptionVisible(selfDescriptionVisible);
        setErrorMessageVisible(selfErrorVisible);
        stateChangeCallback?.({
            wantToShowDescription: wantToShowDescription || childrensWithVisibleDescriptions.current.size > 0,
            wantToShowErrorMessage: wantToShowErrorMessage || childrensWithVisibleErrors.current.size > 0,
        }, id);
    }, [wantToShowDescription, parentErrorVisible, wantToShowErrorMessage, stateChangeCallback, id]);
    useEffect(() => {
        updateState();
    }, [updateState]);
    const setChildrenHelperState = useCallback((state, id) => {
        const withVisibleErrorsCount = childrensWithVisibleErrors.current.size;
        const withVisibleDescriptionsCount = childrensWithVisibleDescriptions.current.size;
        /* eslint-disable @typescript-eslint/no-unused-expressions */
        state.wantToShowDescription
            ? childrensWithVisibleDescriptions.current.add(id)
            : childrensWithVisibleDescriptions.current.delete(id);
        state.wantToShowErrorMessage
            ? childrensWithVisibleErrors.current.add(id)
            : childrensWithVisibleErrors.current.delete(id);
        /* eslint-enable @typescript-eslint/no-unused-expressions */
        if (withVisibleErrorsCount !== childrensWithVisibleErrors.current.size ||
            withVisibleDescriptionsCount !== childrensWithVisibleDescriptions.current.size) {
            updateState();
        }
    }, [updateState]);
    return {
        descriptionVisible,
        errorMessageVisible,
        setChildrenHelperState,
        wantToShowErrorMessage,
        wantToShowDescription,
    };
};

export { FormHelperStateProvider, useFormHelperState };
//# sourceMappingURL=useFormHelperState.js.map
